import { type Action, type Dispatch, isAnyOf, type TypedStartListening } from '@reduxjs/toolkit';

import { authActions } from '../../flux/auth';
import { receiptsActions, receiptsSelectors } from '../../flux/receipts';
import {
  getReceiptProcessingTransitions,
  type ReceiptOriginLocation,
  trackReceiptEvent,
} from '../../flux/receipts/receiptsTracking';
import type { RECEIPTS_REDUCER_KEY } from '../../flux/receipts/receiptsTypes';
import type RootState from '../../utils/redux-store/rootState';

type RequiredState = Pick<RootState, typeof RECEIPTS_REDUCER_KEY>;
type AppStartListening = TypedStartListening<RequiredState, Dispatch<Action>>;

const isSessionBoundary = isAnyOf(authActions.authLogout, authActions.authSelectDataset);
const isTargetedLinkResult = isAnyOf(
  receiptsActions.linkTargetedReceiptToTransaction.fulfilled,
  receiptsActions.linkTargetedReceiptToTransaction.rejected,
);

export const setupReceiptProcessingTrackingListener = (startAppListening: AppStartListening): void => {
  // Closing the editor clears a receipt's target before processing finishes, so remember where each upload
  // started. Both keys are kept: an upload replaces the document id, then the created receipt replaces the local
  // id. Receipts this session never saw start, such as ones restored after a restart, have no origin here.
  const originsByKey = new Map<string, ReceiptOriginLocation>();

  startAppListening({
    predicate: (_action, currentState, previousState) => currentState.receiptsStore !== previousState.receiptsStore,
    effect(action, listenerApi) {
      const previousReceipts = receiptsSelectors.selectAllReceipts(listenerApi.getOriginalState());
      const currentReceipts = receiptsSelectors.selectAllReceipts(listenerApi.getState());
      const didExpireStaleReceipts = receiptsActions.expireStalePendingReceipts.match(action);

      getReceiptProcessingTransitions(previousReceipts, currentReceipts, didExpireStaleReceipts, originsByKey).forEach(
        (properties) => trackReceiptEvent({ event: 'receiptProcessingResult', properties }),
      );

      // A retry restages the same local id, so an origin seen earlier this session is kept.
      if (receiptsActions.addPendingReceipt.match(action) && !originsByKey.has(action.payload.id)) {
        originsByKey.set(
          action.payload.id,
          action.payload.targetTransactionId ? 'transaction-detail' : 'receipt-center',
        );
      }

      currentReceipts.forEach((receipt) => {
        const origin = receipt.targetTransactionId
          ? 'transaction-detail'
          : (originsByKey.get(receipt.id) ?? originsByKey.get(receipt.documentId));
        if (origin) {
          originsByKey.set(receipt.id, origin);
          originsByKey.set(receipt.documentId, origin);
        }
      });
    },
  });

  startAppListening({
    matcher: isTargetedLinkResult,
    effect(action, listenerApi) {
      if (
        receiptsActions.linkTargetedReceiptToTransaction.rejected.match(action) &&
        action.payload === 'STALE_SESSION'
      ) {
        return;
      }
      // Like the reducer, skip a result whose target a session boundary cleared while the link was in flight.
      const { id, transactionId } = action.meta.arg;
      if (listenerApi.getOriginalState().receiptsStore.entities[id]?.targetTransactionId !== transactionId) return;

      trackReceiptEvent({
        event: 'receiptTransactionLinkResult',
        properties: {
          location: 'transaction-detail',
          result: receiptsActions.linkTargetedReceiptToTransaction.fulfilled.match(action) ? 'success' : 'failed',
        },
      });
    },
  });

  startAppListening({
    matcher: isSessionBoundary,
    effect() {
      originsByKey.clear();
    },
  });
};
